import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Kartoffelautomat kaufen“ (/kartoffelautomat).
 * Ziel-Keyword „kartoffelautomat“ (~200/Monat, geringer Wettbewerb, hoher CPC).
 * Muster/SEO wie die übrigen Cluster-Seiten. Ambient-Produkt → keine Kühlung nötig.
 */
@Component({
  selector: 'app-kartoffelautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './kartoffelautomat-page.component.html',
  styleUrls: ['./kartoffelautomat-page.component.scss', '../cluster-landing.shared.scss'],
})
export class KartoffelautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'wege-der-landwirtschaftlichen-direktvermarktung';
  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;
  /** Öffentlicher Live-Link einer Box mit u. a. Kartoffeln. */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/kartoffel-mueller/U-MB-00007'
  );

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@kta.faq1.q:Was kostet ein Kartoffelautomat?`,
      a: $localize`:@@kta.faq1.a:Die marktbox als Kartoffelautomat gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@kta.faq2.q:Welche Produkte kann ich neben Kartoffeln verkaufen?`,
      a: $localize`:@@kta.faq2.a:Neben Kartoffeln eignen sich alle ungekühlten Feldfrüchte und Hofprodukte: Zwiebeln, Kürbis, Äpfel, Eier, Honig oder Eingemachtes. Gekühlte Waren sind aktuell nicht möglich, da keine Kühlung verfügbar ist.`,
    },
    {
      q: $localize`:@@kta.faq3.q:Kartoffelautomat kaufen oder mieten?`,
      a: $localize`:@@kta.faq3.a:Beides ist möglich. Kaufen lohnt sich bei dauerhaftem Betrieb, Mieten senkt die Einstiegshürde. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@kta.faq4.q:Wo kann ich einen Kartoffelautomaten aufstellen?`,
      a: $localize`:@@kta.faq4.a:Die marktbox lässt sich flexibel aufstellen – am Feldrand, direkt ab Hof oder an gut sichtbaren Standorten mit Laufkundschaft. Wichtig sind Sichtbarkeit und einfache Erreichbarkeit.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@kta.metaTitle:Kartoffelautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@kta.metaDesc:Kartoffelautomat kaufen oder mieten: Kartoffeln & Feldfrüchte 24/7 verkaufen – ab Hof oder Feldrand. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@kta.metaKeywords:Kartoffelautomat, Kartoffelautomat kaufen, Kartoffeln verkaufen Automat, Feldrand Automat, Direktvermarktung, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('kartoffel-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Kartoffelautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('kartoffel-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/kartoffelautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
